import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  createApiToken,
  deleteApiToken,
} from '@sportninja/common/services/account'
import type {
  ApiTokenPayload,
  CreatedApiToken,
} from '@sportninja/common/features/account/apiAccess'

/**
 * Create / delete an API token. Each refreshes the token list. `retry: 0`: a
 * retried create would mint a second token whose secret nobody ever sees.
 */
export const useApiTokenMutations = (accountUid: string) => {
  const queryClient = useQueryClient()
  const refreshList = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.accountApiAccess.apiTokens(accountUid),
    })

  const create = useMutation<CreatedApiToken, Error, ApiTokenPayload>({
    mutationFn: (payload) => createApiToken(accountUid, payload),
    onSuccess: refreshList,
    retry: 0,
  })

  const remove = useMutation<void, Error, number>({
    mutationFn: (apiTokenId) => deleteApiToken(accountUid, apiTokenId),
    onSuccess: refreshList,
    retry: 0,
  })

  return { create, remove }
}
